import React, { useEffect, useState } from "react";
import axios from "axios";
import Logo from "../../utils/salesDood2.png";
import useApi from "../../hooks/useApi";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  ChevronDown,
  Eye,
  EyeOff,
  Mail,
  MapPin,
  Phone,
  Settings,
  Shield,
  Users,
  Zap,
} from "lucide-react";

const LoginPage = () => {
  const [loginUserName, setLoginUserName] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showContact, setShowContact] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isFetchingCompanies, setIsFetchingCompanies] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const navigate = useNavigate();
  const MASTER_COMPANY_CODE = localStorage?.getItem("masterCompanyCode") || "-";

  const checkIfAlreadyLoggedIn = () => {
    const loginStatus = JSON.parse(
      localStorage.getItem("currentUserLoginStatus"),
    );

    if (loginStatus) {
      navigate("/home");
    }
  };

  const getSubCompanies = async () => {
    if (!MASTER_COMPANY_CODE) return;
    const previousCompanyCode = localStorage.getItem("companyCode");
    setIsFetchingCompanies(true);
    try {
      // Use a default base URL for initial company fetch
      const defaultBaseUrl = "https://cubixweberp.com:213";
      const response = await axios.get(
        `${defaultBaseUrl}/api/companylist/${MASTER_COMPANY_CODE}`,
      );
      if (response.data && response.data.length > 0) {
        setCompanies(response.data);
        setSelectedCompany(
          previousCompanyCode
            ? response.data?.find(
                (company) => company?.COMPID === previousCompanyCode,
              )
            : response.data[0],
        );
      }
    } catch (err) {
      console.error("Failed to fetch companies", err);
    } finally {
      setIsFetchingCompanies(false);
    }
  };

  useEffect(() => {
    checkIfAlreadyLoggedIn();
    getSubCompanies();
  }, []);

  const togglePasswordVisibility = (e) => {
    e.preventDefault();
    setShowPassword((prevState) => !prevState);
  };

  const getApiPaths = async (companyCode) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}:213/api/clientSingle/${companyCode}`,
      );

      if (response.data?.length > 0) {
        localStorage.setItem("apiPaths", JSON.stringify(response.data));
        return response.data;
      } else {
        localStorage.setItem("apiPaths", JSON.stringify([]));
        return null;
      }
    } catch (err) {
      console.error("Failed to fetch API paths:", err);
      return null;
    }
  };

  const { request, error, loading, cancel } = useApi();

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setErrorMessage("");

    const targetCompanyCode = selectedCompany?.COMPID || MASTER_COMPANY_CODE;

    try {
      // Step 1: Fetch API paths for the selected company
      console.log("Fetching API paths for company:", targetCompanyCode);
      const apiPaths = await getApiPaths(targetCompanyCode);

      if (!apiPaths || apiPaths.length === 0) {
        setErrorMessage("Failed to fetch API configuration. Please try again.");
        setIsLoggingIn(false);
        return;
      }

      const BASE_URL = apiPaths[0].API_CONFIG || "https://cubixweberp.com:213";
      console.log("Base URL:", BASE_URL);

      // Step 2: Proceed with login using the fetched API path
      const apiurl = `${BASE_URL}/api/LoginUser?user=${loginUserName}&pass=${password}&cmpcode=${targetCompanyCode}`;

      const loginData = await request({
        url: apiurl,
        method: "GET",
        timeout: 5000,
      });

      const successUser = loginData.find((user) => user.Userlogin === "Sucess");
      console.log("Success user LOGIN", successUser);
      const userEmployeeID = loginData[0]?.EMPID;
      const userFirstDeptNo = loginData[0]?.DEPTNO;

      if (successUser) {
        if (userFirstDeptNo?.trim()) {
          // Store all necessary information
          localStorage.setItem("companyCode", targetCompanyCode);
          localStorage.setItem("currentUser", JSON.stringify(loginData));
          localStorage.setItem("currentUserName", loginUserName?.trim());
          localStorage.setItem("currentUserEmployeeID", userEmployeeID?.trim());
          localStorage.setItem(
            "currentSelectedDepartment",
            userFirstDeptNo?.trim(),
          );
          localStorage.setItem("currentUserLoginStatus", JSON.stringify(true));

          console.log("Login successful, navigating...");
          console.log({ loginData });
          navigate("/home");
        } else {
          setErrorMessage(
            "Department number is missing. Please contact admin.",
          );
        }
      } else {
        setErrorMessage("Invalid credentials. Please try again.");
      }
    } catch (err) {
      if (err.code === "ECONNABORTED") {
        setErrorMessage("Login request timed out. Please try again.");
      } else if (err.name === "CanceledError") {
        setErrorMessage("Login request was canceled.");
      } else {
        setErrorMessage("Login failed. Please check your credentials.");
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="max-w-4xl w-full bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
        <div className="grid md:grid-cols-2 gap-0">
          <div
            className="p-8 text-white relative overflow-hidden "
            style={{
              background: `
                radial-gradient(circle, rgba(255,255,255,0.2) 1px, transparent 1px),
                linear-gradient(to bottom right, #2563eb, #4f46e5)
              `,
              backgroundSize: "20px 20px, auto",
            }}
          >
            <div className="flex items-center justify-center mb-6">
              <img src={Logo} alt="salesdood-logo" className="h-14" />
            </div>

            <div className="mb-6">
              <h2 className="text-xl font-bold mb-3">Welcome Back,</h2>
              <p className="text-indigo-100 text-sm leading-relaxed mb-4">
                Power your business with a unified ERP solution. Optimize every
                process and transform real-time data into a competitive
                advantage.{" "}
              </p>

              <div className="space-y-3">
                <div className="flex items-center space-x-3">
                  <Shield className="w-5 h-5 text-indigo-200" />
                  <span className="text-sm text-indigo-100">
                    Advanced Security
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <Zap className="w-5 h-5 text-indigo-200" />
                  <span className="text-sm text-indigo-100">
                    Instant Insights
                  </span>
                </div>
                <div className="flex items-center space-x-3">
                  <Settings className="w-5 h-5 text-indigo-200" />
                  <span className="text-sm text-indigo-100">
                    Customizable Workflows{" "}
                  </span>
                </div>
              </div>
            </div>

            <div className="mb-6">
              <h3 className="font-semibold mb-3">Why Choose Us?</h3>
              <ul className="space-y-2 text-sm text-indigo-100">
                <li className="flex items-center space-x-2">
                  <span className="w-1.5 h-1.5 bg-indigo-200 rounded-full"></span>
                  <span>Seamless Lead Tracking</span>
                </li>
                <li className="flex items-center space-x-2">
                  <span className="w-1.5 h-1.5 bg-indigo-200 rounded-full"></span>
                  <span>Automated Workflows</span>
                </li>
                <li className="flex items-center space-x-2">
                  <span className="w-1.5 h-1.5 bg-indigo-200 rounded-full"></span>
                  <span>Customizable Dashboards</span>
                </li>
                <li className="flex items-center space-x-2">
                  <span className="w-1.5 h-1.5 bg-indigo-200 rounded-full"></span>
                  <span>Cross-Platform Access</span>
                </li>
              </ul>
            </div>

            <div className="border-t border-indigo-200 pt-4">
              <button
                onClick={() => setShowContact(!showContact)}
                className="text-sm text-indigo-100 hover:text-white underline mb-3"
              >
                {showContact ? "Hide Contact Info" : "Show Contact Info"}
              </button>

              {showContact && (
                <div className="space-y-3 text-sm">
                  <div className="flex items-center space-x-3">
                    <Phone className="w-5 h-5 text-indigo-200" />
                    <span className="text-indigo-100">+971 4 2677221</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Mail className="w-5 h-5 text-indigo-200" />
                    <span className="text-indigo-100">
                      info@cubixitsolutions.com
                    </span>
                  </div>
                  <div className="flex items-start space-x-3">
                    <MapPin className="w-5 h-5 text-indigo-200 mt-0.5" />
                    <span className="text-indigo-100">
                      Al Nazriya Buildings - Suite 129 - <br /> Damascus Street
                      - Al Qusais - Dubai
                    </span>
                  </div>
                  <p className="text-xs text-indigo-200 mt-2">
                    Business Hours: Mon-Sat 9AM-6PM EST
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="p-8 bg-gray-50 flex flex-col justify-center">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Sign In</h2>
              <p className="text-gray-600 text-sm">
                Log in to access your Salesdood account
              </p>
            </div>

            {errorMessage && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-6">
                <p className="text-sm text-red-600">{errorMessage}</p>
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Select Company *
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Building2 className="h-4 w-4 text-gray-400 group-focus-within:text-indigo-500 transition-colors" />
                  </div>
                  <select
                    value={selectedCompany?.COMPID || ""}
                    onChange={(e) => {
                      const comp = companies.find(
                        (c) => c.COMPID === e.target.value,
                      );
                      setSelectedCompany(comp);
                    }}
                    className="block w-full pl-10 pr-10 py-3 text-sm border border-gray-300 rounded-lg appearance-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white outline-none transition-all cursor-pointer"
                    disabled={isFetchingCompanies}
                  >
                    {isFetchingCompanies ? (
                      <option>Loading companies...</option>
                    ) : (
                      companies.map((comp) => (
                        <option key={comp.COMPID} value={comp.COMPID}>
                          {comp.COMPNAME}
                          
                          {/* {comp.COMPID} - {comp.COMPNAME} */}
                        </option>
                      ))
                    )}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                    <ChevronDown className="h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>

              {/* Username Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Username *
                </label>
                <div className="relative flex items-center">
                  <input
                    type="text"
                    value={loginUserName}
                    onChange={(e) => setLoginUserName(e.target.value)}
                    placeholder="Enter your username"
                    className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                    required
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Password *
                </label>
                <div className="relative flex items-center">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all"
                    required
                    id="loginPassword"
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute right-3 text-indigo-600"
                  >
                    {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}
                  </button>
                </div>
              </div>

              <div className="flex justify-between items-center">
                <button
                  type="button"
                  className="text-sm text-indigo-600 hover:underline"
                >
                  Forgot Password?
                </button>
              </div>

              <button
                type="submit"
                disabled={isLoggingIn}
                className={`w-full bg-indigo-600 text-white py-3 px-4 rounded-lg text-sm font-medium transition-colors
                  ${
                    isLoggingIn
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-indigo-700"
                  }`}
              >
                {isLoggingIn ? (
                  <span className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Logging In...</span>
                  </span>
                ) : (
                  "Sign In"
                )}
              </button>
            </form>

            <div className="mt-6 pt-4 border-t border-gray-200 text-center">
              <p className="text-sm text-gray-600">
                Don't have an account?{" "}
                <button
                  type="button"
                  className="text-indigo-600 hover:underline"
                >
                  Sign up
                </button>
              </p>
              <p className="text-xs text-gray-400 mt-2">
                @2021 Cubix IT Solutions LLC. All rights reserved.{" "}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
